// routeUtils.js
import React from 'react';
import { jwtDecode } from 'jwt-decode';
import { additionalRoutes, baseRoutes, clientRoutes } from 'routes/routeConfig';

export const getClientName = () => {
  const domain = window.location.hostname;
  return domain?.split('.')[0].toUpperCase();
};

export const getTenantFromToken = () => {
  const token = localStorage.getItem('auth_tokens');
  if (!token) return null;

  try {
    const parsedToken = JSON.parse(token);
    if (parsedToken && parsedToken.access_token) {
      const decodedToken = jwtDecode(parsedToken.access_token);
      return decodedToken?.tenant_id;
    }
  } catch (error) {
    console.error('Error parsing token:', error);
  }
  return null;
};

export const getTenantDetails = () => {
  const token = localStorage.getItem('auth_tokens');
  if (!token) return null;

  try {
    const parsedToken = JSON.parse(token);
    if (parsedToken && parsedToken.access_token) {
      const decodedToken = jwtDecode(parsedToken.access_token);
      return decodedToken;
    }
  } catch (error) {
    console.error('Error parsing token for tenant details:', error);
  }
  return null;
};

/**
 * Utility function to safely render an icon component
 * @param {Function|Object|null} IconComponent - The icon component to render
 * @param {Object} props - Additional props to pass to the icon
 * @returns {JSX.Element|null} - Rendered icon or null
 */
export const renderIcon = (IconComponent, props = {}) => {
  if (!IconComponent) return null;

  // If it's a function/component, render it
  if (typeof IconComponent === 'function') {
    return <IconComponent {...props} />;
  }

  // If it's already a rendered element, return it
  if (React.isValidElement(IconComponent)) {
    return IconComponent;
  }

  // Otherwise return null
  return null;
};

/**
 * Recursively processes a navigation item and its children to any depth
 * @param {Object|string} item - The navigation item to process
 * @param {number} depth - Current recursion depth (for debugging/limits)
 * @param {number} maxDepth - Maximum allowed recursion depth (safety limit)
 * @returns {Object|null} - Processed route object or null if invalid
 */
const processNavigationItem = (item, depth = 0, maxDepth = 15) => {
  // Safety check to prevent infinite recursion
  if (depth > maxDepth) {
    console.warn(
      `Maximum recursion depth (${maxDepth}) reached for item:`,
      item
    );
    return null;
  }

  // Handle string items - simple route lookup
  if (typeof item === 'string') {
    const route = additionalRoutes[item];
    if (route) {
      return { ...route };
    }
    // Return fallback route if not found in additionalRoutes
    return {
      name: item,
      path: item.toLowerCase(),
      active: true,
      icon: null
    };
  }

  // Handle object items with potential nested children
  if (typeof item === 'object' && item !== null && item.name) {
    const routeKey = item.name;
    const baseRoute = additionalRoutes[routeKey];

    // Start with the base route or create a fallback
    const processedRoute = baseRoute
      ? { ...baseRoute }
      : {
          name: routeKey,
          path: routeKey.toLowerCase(),
          active: true,
          icon: null
        };

    // Process children recursively if they exist
    if (item.children && Array.isArray(item.children)) {
      const processedChildren = item.children
        .map(child => processNavigationItem(child, depth + 1, maxDepth))
        .filter(child => child !== null); // Remove any null results

      if (processedChildren.length > 0) {
        processedRoute.children = processedChildren;
      }
    }

    // Process items array if it exists (for dashboard overview items)
    if (item.items && Array.isArray(item.items)) {
      const processedItems = item.items
        .map(subItem => processNavigationItem(subItem, depth + 1, maxDepth))
        .filter(subItem => subItem !== null);

      if (processedItems.length > 0) {
        processedRoute.items = processedItems;
      }
    }

    return processedRoute;
  }

  // Return null for invalid items
  console.warn('Invalid navigation item:', item);
  return null;
};

export const generateClientRoutes = (clientName, tenantId) => {
  if (!clientName || !tenantId) return [];

  const processRoute = (route, tenantId) => {
    const processedRoute = {
      name: route.name,
      icon: route.icon || null, // Store the icon component, don't render it here
      to: `/${tenantId}/${route.path || route.name?.toLowerCase() || ''}`,
      active: route.active !== undefined ? route.active : true,
      img: route.img || null
    };

    // Handle nested children recursively
    if (route.children && Array.isArray(route.children)) {
      processedRoute.children = route.children.map(childRoute =>
        processRoute(childRoute, tenantId)
      );
    }

    // Handle items array (for dashboard components)
    if (route.items && Array.isArray(route.items)) {
      processedRoute.items = route.items.map(item =>
        processRoute(item, tenantId)
      );
    }

    return processedRoute;
  };

  // Retrieve nav_items from localStorage
  const navItemsRaw = localStorage.getItem('nav_items');
  let navItems = [];

  try {
    let navItem = navItemsRaw ? JSON.parse(navItemsRaw) : [];
    navItems = [...navItem];
  } catch (error) {
    console.error('Error parsing nav_items from localStorage:', error);
  }

  const routes = [];

  if (navItems.length > 0) {
    // Use nav_items from localStorage for dynamic routes
    navItems.forEach(item => {
      // Use the enhanced recursive processing
      const processedItem = processNavigationItem(item);
      if (processedItem) {
        routes.push(processRoute(processedItem, tenantId));
      }
    });
  } else {
    // If no nav_items in localStorage, use client-specific additional routes
    const clientConfig = clientRoutes[clientName];
    if (clientConfig && clientConfig.additionalRoutes) {
      clientConfig.additionalRoutes.forEach(routeKey => {
        const route = additionalRoutes[routeKey];
        if (route) {
          routes.push(processRoute(route, tenantId));
        }
      });
    }
  }

  // Always append baseRoutes (e.g. Input Marketplace) so they appear for all users
  Object.values(baseRoutes).forEach(route => {
    // Resolve string children to their route definitions from additionalRoutes
    const resolvedRoute = { ...route };
    if (resolvedRoute.children && Array.isArray(resolvedRoute.children)) {
      resolvedRoute.children = resolvedRoute.children
        .map(child => {
          if (typeof child === 'string') {
            return additionalRoutes[child] || null;
          }
          return child;
        })
        .filter(Boolean);
    }
    routes.push(processRoute(resolvedRoute, tenantId));
  });

  return [
    {
      label: '',
      labelDisable: false,
      children: routes
    }
  ];
};

/**
 * Utility function to flatten nested routes for easier processing
 * @param {Array} routes - Array of route objects
 * @param {number} maxDepth - Maximum depth to flatten
 * @returns {Array} - Flattened array of routes
 */
export const flattenRoutes = (routes, maxDepth = 15) => {
  const flatten = (routeArray, depth = 0) => {
    if (depth > maxDepth) return [];

    let flattened = [];

    routeArray.forEach(route => {
      flattened.push(route);

      if (route.children) {
        flattened = flattened.concat(flatten(route.children, depth + 1));
      }

      if (route.items) {
        flattened = flattened.concat(flatten(route.items, depth + 1));
      }
    });

    return flattened;
  };

  return flatten(routes);
};

/**
 * Utility function to find a route by name in nested structure
 * @param {Array} routes - Array of route objects to search
 * @param {string} routeName - Name of the route to find
 * @param {number} maxDepth - Maximum search depth
 * @returns {Object|null} - Found route object or null
 */
export const findRouteByName = (routes, routeName, maxDepth = 15) => {
  const search = (routeArray, targetName, depth = 0) => {
    if (depth > maxDepth) return null;

    for (const route of routeArray) {
      if (route.name === targetName) {
        return route;
      }

      if (route.children) {
        const found = search(route.children, targetName, depth + 1);
        if (found) return found;
      }

      if (route.items) {
        const found = search(route.items, targetName, depth + 1);
        if (found) return found;
      }
    }

    return null;
  };

  return search(routes, routeName);
};

/**
 * Utility function to get the depth of nesting in a route structure
 * @param {Array|Object} routes - Route structure to analyze
 * @returns {number} - Maximum depth found
 */
export const getRouteDepth = routes => {
  const calculateDepth = (item, currentDepth = 0) => {
    let maxDepth = currentDepth;

    if (Array.isArray(item)) {
      item.forEach(route => {
        const depth = calculateDepth(route, currentDepth);
        maxDepth = Math.max(maxDepth, depth);
      });
    } else if (item && typeof item === 'object') {
      if (item.children) {
        const childDepth = calculateDepth(item.children, currentDepth + 1);
        maxDepth = Math.max(maxDepth, childDepth);
      }
      if (item.items) {
        const itemDepth = calculateDepth(item.items, currentDepth + 1);
        maxDepth = Math.max(maxDepth, itemDepth);
      }
    }

    return maxDepth;
  };

  return calculateDepth(routes);
};
